import React, { useEffect, useMemo, useState } from 'react';
import { useSheet } from '../hooks/useSheet';
import { getMemberAccessToken } from '../auth/supabase';
import { personNamesMatch } from '../tracking/deploymentUsageModel';
import { LEGACY_DEPLOYMENT_INCIDENTS } from './legacyDeploymentIncidents';
import './availability.css';

function clean(value) {
  return String(value ?? '').trim();
}

function statusClass(value) {
  const text = clean(value).toLowerCase();
  if (text === 'yes') return 'available';
  if (text.includes('deployed')) return 'deployed';
  if (text === 'no') return 'unavailable';
  if (text.includes('returned')) return 'returned';
  return 'neutral';
}

function statusLabel(value) {
  const text = clean(value);
  return text || 'Not set';
}

function isTrainee(value) {
  const text = clean(value).toLowerCase();
  return text === 'yes' || text.includes('trainee');
}

function dateKey(value) {
  const text = clean(value);
  if (!text || text.toLowerCase() === 'n/a') return '';

  // Google Visualization API returns Sheet dates as Date(year, zeroBasedMonth, day).
  const gviz = /^Date\((\d+),(\d+),(\d+)\)$/.exec(text);
  if (gviz) {
    return `${gviz[1]}-${String(Number(gviz[2]) + 1).padStart(2, '0')}-${String(Number(gviz[3])).padStart(2, '0')}`;
  }

  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (ymd) return `${ymd[1]}-${ymd[2]}-${ymd[3]}`;

  const mdy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text);
  if (mdy) return `${mdy[3]}-${String(Number(mdy[1])).padStart(2, '0')}-${String(Number(mdy[2])).padStart(2, '0')}`;

  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return '';
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
}

function dayStamp(value) {
  const key = dateKey(value);
  if (!key) return null;
  const [year, month, day] = key.split('-').map(Number);
  return Date.UTC(year, month - 1, day);
}

function formatDate(value) {
  const key = dateKey(value);
  if (!key) return '';
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day, 12, 0, 0, 0)
    .toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

function deploymentForRosterDate(name, sheetDate, deployments) {
  const target = dayStamp(sheetDate);
  if (target === null) return null;

  return (deployments || [])
    .filter(item => personNamesMatch(item?.person, name))
    .map(item => {
      const start = dayStamp(item?.startDate || item?.createdAt);
      const end = dayStamp(item?.returnedAt || (item?.status !== 'closed' ? new Date() : item?.updatedAt || item?.startDate || item?.createdAt));
      if (start === null && end === null) return null;
      const low = Math.min(start ?? end, end ?? start);
      const high = Math.max(start ?? end, end ?? start);
      const exactBoundary = target === start || target === end;
      const containsDate = target >= low && target <= high;
      if (!exactBoundary && !containsDate) return null;
      return {
        item,
        exactBoundary,
        duration: Math.max(0, high - low),
      };
    })
    .filter(Boolean)
    .sort((a, b) => Number(b.exactBoundary) - Number(a.exactBoundary) || a.duration - b.duration)[0]?.item || null;
}

function legacyIncidentFor(name, sheetDate) {
  const key = dateKey(sheetDate);
  if (!key) return null;
  return LEGACY_DEPLOYMENT_INCIDENTS.find(entry => entry.date === key && personNamesMatch(entry.person, name)) || null;
}

export default function AvailabilityRoster({ sheetId, sheetName }) {
  const rows = useSheet(sheetId, sheetName);
  const [tracking, setTracking] = useState({ status: 'loading', deployments: [], error: '' });

  useEffect(() => {
    let cancelled = false;
    getMemberAccessToken()
      .then(token => {
        if (!token) throw new Error('Member sign in required.');
        return fetch('/api/member-tracking-state', {
          cache: 'no-store',
          headers: { Authorization: `Bearer ${token}` },
        });
      })
      .then(async response => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload?.error || 'Unable to load deployment history.');
        if (!cancelled) setTracking({ status: 'ready', deployments: Array.isArray(payload?.deployments) ? payload.deployments : [], error: '' });
      })
      .catch(error => {
        if (!cancelled) setTracking({ status: 'error', deployments: [], error: error?.message || 'Deployment history unavailable.' });
      });
    return () => { cancelled = true; };
  }, []);

  const people = useMemo(() => (rows || [])
    .filter(row => !['ignore', 'protect'].includes(clean(row?.[9]).toLowerCase()))
    .filter(row => clean(row?.[0]) && clean(row?.[0]).toLowerCase() !== 'n/a')
    .map(row => {
      const name = clean(row[0]);
      const sheetLastDeployment = clean(row[5]);
      const matchedDeployment = deploymentForRosterDate(name, sheetLastDeployment, tracking.deployments);
      const legacyIncident = legacyIncidentFor(name, sheetLastDeployment);
      return {
        name,
        rank: clean(row[1]),
        availability: clean(row[2]),
        trainee: clean(row[3]),
        sheetLastDeployment,
        matchedDeployment,
        legacyIncident,
      };
    }), [rows, tracking.deployments]);

  const availableCount = people.filter(person => clean(person.availability).toLowerCase() === 'yes').length;
  const deployedCount = people.filter(person => clean(person.availability).toLowerCase().includes('deployed')).length;

  return (
    <div className="availability-root">
      <div className="availability-summary">
        <div>
          <span className="availability-kicker">{sheetName === 'Medics' ? 'LINE MEDICS' : 'LINE EMTS'}</span>
          <strong>{people.length}</strong>
          <small>Rostered</small>
        </div>
        <div>
          <span className="availability-kicker">AVAILABLE NOW</span>
          <strong>{availableCount}</strong>
          <small>Marked yes</small>
        </div>
        <div>
          <span className="availability-kicker">DEPLOYED</span>
          <strong>{deployedCount}</strong>
          <small>Currently out</small>
        </div>
      </div>

      {tracking.status === 'error' && (
        <div className="availability-note">Roster dates are live from Google Sheets. App deployment history could not be loaded, so some incident names may be unavailable.</div>
      )}

      <div className="availability-roster-grid">
        {people.map(person => {
          const lastDate = formatDate(person.sheetLastDeployment);
          const trackedIncident = clean(person.matchedDeployment?.incident);
          const incident = trackedIncident || clean(person.legacyIncident?.incident);
          const deploymentType = clean(person.matchedDeployment?.deploymentType || person.matchedDeployment?.role);
          const activeMatch = person.matchedDeployment?.status !== 'closed' && clean(person.availability).toLowerCase().includes('deployed');

          return (
            <article className="availability-person-card" key={`${sheetName}-${person.name}`}>
              <div className="availability-person-head">
                <div>
                  <h3>{person.name}</h3>
                  <p>{person.rank || (sheetName === 'Medics' ? 'Line Medic' : 'Line EMT')}{isTrainee(person.trainee) ? ' · Trainee' : ''}</p>
                </div>
                <span className={`availability-status availability-status--${statusClass(person.availability)}`}>{statusLabel(person.availability)}</span>
              </div>

              <div className="availability-person-detail">
                <span>LAST DEPLOYMENT</span>
                {lastDate ? (
                  <>
                    <strong>{incident || 'Incident not recorded'}</strong>
                    <small>{lastDate}{deploymentType ? ` · ${deploymentType}` : ''}{activeMatch ? ' · Active' : ''}</small>
                  </>
                ) : (
                  <>
                    <strong>No deployment date recorded</strong>
                    <small>The Google availability roster has no last-deployment date for this member.</small>
                  </>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {!people.length && (
        <div className="availability-empty">
          {sheetName === 'EMTs' ? 'No Line EMT members are currently rostered.' : 'Loading availability roster…'}
        </div>
      )}
    </div>
  );
}
